import React, {
  Suspense,
  useMemo,
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  type RefObject,
} from 'react'
import { Canvas, useThree, type ThreeEvent } from '@react-three/fiber'
import { OrbitControls, useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { getProduct } from '~/components/ThreeDModel/products'
import { GroundShadow } from '~/components/ThreeDModel/GroundShadow'
import { GLBModel } from '~/components/ThreeDModel/GLBModel'
import { captureScreenshot } from '~/utils/screenshotUtils'
import { exportSceneAsGLB, triggerDownload } from '~/utils/exportModelUtils'
import { ColorName, getColorItemByName } from '~/utils/colorDictionary'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import styles from './WorkTableViewer.module.css'
import { ViewerZoomControls } from '~/components/ViewerZoomControls/ViewerZoomControls'
import { WebGLErrorBoundary } from '~/components/WebGLErrorBoundary/WebGLErrorBoundary'
import { ModelLoadingOverlay } from '~/components/ThreeDModel/ModelLoadingOverlay'
import { useSceneDebug } from '~/components/ThreeDModel/SceneDebugInspector'
import { SidePanels } from '../ThreeDModel/parts/SidePanels'
import { Drawer } from '../ThreeDModel/parts/Drawer'
import { usePanel } from '~/hooks/usePanel'
import { FURNITURE_CONFIG, OpeningType } from '../ThreeDModel/furnitureConfig'
import { usePanelSource } from '../ThreeDModel/three/proceduralPanelSource'
import { ensurePlanarUVs } from '../ThreeDModel/uvUtils'
import {
  WORK_TABLE_GEOMETRY,
  workTableInnerWidth,
  workTableDrawerDimensions,
} from '~/utils/workTableGeometry'

type Vec3 = [number, number, number]

const MODEL_URLS = {
  workTop: '/cdn/assets/3d-models/work-table-top.glb',
  profileHandle: '/cdn/assets/3d-models/profile-handle.glb',
  monitor: '/cdn/assets/3d-models/monitor.glb',
} as const

Object.values(MODEL_URLS).forEach((url) => {
  useGLTF.preload(url)
})

const MONITOR = {
  scale: 90,
  positionZ: 9,
  yOffsetFromTop: -6,
} as const

const FALLBACK_COLOR =
  getColorItemByName(ColorName.GreenSalvia)?.hexCode ?? '#8FA68A'

const CAMERA_CONFIG = {
  position: [160, 127.18, 365.75] as Vec3,
  fov: 45,
  near: 1,
  far: 15000,
} as const

const ORBIT_CONFIG = {
  minDistance: 100,
  maxDistance: 300,
  minAzimuthAngle: -Math.PI / 4 ,
  maxAzimuthAngle: Math.PI / 2 - 0.5,
  minPolarAngle:  Math.PI/4,
  maxPolarAngle: Math.PI /2.4,
} as const

export interface WorkTableViewerRef {
  captureScreenshot: (
    format?: 'image/png' | 'image/jpeg',
    quality?: number
  ) => string | null
  exportModel: () => Promise<void>
}

interface WorkTableViewerProps {
  width: number
  depth: number
  height: number
  selectedColor?: string
  hasDrawers?: boolean
  drawerCount?: number
  onShare?: () => void
  onExport?: () => Promise<void> | void
}

interface WorkTableModelProps {
  width: number
  depth: number
  height: number
  color: string
  hasDrawers: boolean
  drawerCount: number
}

const WorkTableModel: React.FC<WorkTableModelProps> = ({
  width,
  depth,
  height,
  color,
  hasDrawers,
  drawerCount,
}) => {
  const horizontalPanelObject = usePanelSource()
  const { scene: workTableTopRaw } = useGLTF(MODEL_URLS.workTop)
  const { scene: profileHandleObject } = useGLTF(MODEL_URLS.profileHandle)
  const { scene: monitorObject } = useGLTF(MODEL_URLS.monitor)

  const workTableTopObject = useMemo(() => {
    ensurePlanarUVs(workTableTopRaw)
    return workTableTopRaw
  }, [workTableTopRaw])

  const { panelThickness } = FURNITURE_CONFIG

  const topAnchor = useMemo(
    () => ({ anchorY: 'min' as const, anchorZ: 'min' as const }),
    []
  )

  const innerWidth = workTableInnerWidth(width)

  const topPanelConfig = useMemo(
    () => ({
      scale: [
        innerWidth,
        panelThickness,
        depth - 3 * panelThickness - WORK_TABLE_GEOMETRY.topPanelDepthInset,
      ] as Vec3,
      position: [
        0,
        height - panelThickness - WORK_TABLE_GEOMETRY.topPanelYInset,
        panelThickness,
      ] as Vec3,
      color,
      anchor: topAnchor,
    }),
    [innerWidth, depth, height, panelThickness, color, topAnchor]
  )
  const topPanel = usePanel(workTableTopObject, topPanelConfig)

  const backPanelConfig = useMemo(
    () => ({
      scale: [innerWidth, WORK_TABLE_GEOMETRY.backPanelHeight, panelThickness] as Vec3,
      position: [
        0,
        height - WORK_TABLE_GEOMETRY.backPanelHeight - panelThickness,
        0,
      ] as Vec3,
      color,
      anchor: topAnchor,
    }),
    [innerWidth, height, panelThickness, color, topAnchor]
  )
  const backPanel = usePanel(horizontalPanelObject, backPanelConfig)

  const frontPanelConfig = useMemo(
    () => ({
      scale: [
        innerWidth - WORK_TABLE_GEOMETRY.frontPanelGap,
        WORK_TABLE_GEOMETRY.frontPanelHeight,
        panelThickness,
      ] as Vec3,
      position: [
        0,
        height - WORK_TABLE_GEOMETRY.frontPanelHeight - 3 * panelThickness,
        depth - 2 * panelThickness,
      ] as Vec3,
      color,
      anchor: topAnchor,
    }),
    [innerWidth, height, panelThickness, depth, color, topAnchor]
  )
  const frontPanel = usePanel(horizontalPanelObject, frontPanelConfig)

  const bottomPanelConfig = useMemo(
    () => ({
      scale: [
        WORK_TABLE_GEOMETRY.bottomPanelWidth,
        panelThickness,
        depth - panelThickness,
      ] as Vec3,
      position: [
        0,
        height - WORK_TABLE_GEOMETRY.frontPanelHeight - panelThickness,
        0,
      ] as Vec3,
      color,
      anchor: topAnchor,
    }),
    [panelThickness, height, depth, color, topAnchor]
  )
  const bottomPanel = usePanel(horizontalPanelObject, bottomPanelConfig)

  const monitor = useMemo(() => {
    const cloned = monitorObject.clone(true)
    cloned.scale.setScalar(MONITOR.scale)
    cloned.userData.isMonitor = true
    return cloned
  }, [monitorObject])

  return (
    <group>
      <SidePanels
        horizontalPanelObject={horizontalPanelObject}
        desiredWidth={width}
        desiredHeight={height}
        desiredDepth={depth}
        selectedColor={color}
      />
      {topPanel && <primitive key="work-table-top" object={topPanel} />}
      {backPanel && <primitive key="work-table-back" object={backPanel} />}
      {frontPanel && !hasDrawers && (
        <primitive key="work-table-front" object={frontPanel} />
      )}
      {bottomPanel && !hasDrawers && (
        <primitive key="work-table-bottom" object={bottomPanel} />
      )}
      {hasDrawers && (
        <WorkTableDrawers
          horizontalPanelObject={horizontalPanelObject}
          profileHandleObject={profileHandleObject}
          color={color}
          width={width}
          depth={depth}
          height={height}
          panelThickness={panelThickness}
          drawerCount={drawerCount}
        />
      )}
      <primitive
        key="work-table-monitor"
        object={monitor}
        position={[0, height + MONITOR.yOffsetFromTop, MONITOR.positionZ]}
      />
    </group>
  )
}

interface WorkTableDrawersProps {
  horizontalPanelObject: THREE.Object3D
  profileHandleObject: THREE.Object3D
  color: string
  width: number
  depth: number
  height: number
  panelThickness: number
  drawerCount: number
}

const WorkTableDrawers: React.FC<WorkTableDrawersProps> = ({
  horizontalPanelObject,
  profileHandleObject,
  color,
  width,
  depth,
  height,
  panelThickness,
  drawerCount,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const innerWidth = workTableInnerWidth(width)
  const dims = workTableDrawerDimensions(innerWidth, depth, drawerCount)
  const slotSize = innerWidth / drawerCount
  const drawerPositionY = height - WORK_TABLE_GEOMETRY.drawerHeight - 3 * panelThickness

  const drawerLayout = useMemo(
    () =>
      Array.from({ length: drawerCount }, (_, index) => ({
        index,
        positionX: (index - (drawerCount - 1) / 2) * slotSize,
      })),
    [drawerCount, slotSize]
  )

  const handlePointerOver = useCallback(
    (index: number) => (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation()
      setHoveredIndex(index)
    },
    []
  )

  const handlePointerOut = useCallback((e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation()
    setHoveredIndex(null)
  }, [])

  return (
    <>
      {drawerLayout.map(({ index, positionX }) => (
        <group
          key={`work-table-drawer-${index}`}
          onPointerOver={handlePointerOver(index)}
          onPointerOut={handlePointerOut}
        >
          <Drawer
            horizontalPanelObject={horizontalPanelObject}
            profileHandleObject={profileHandleObject}
            openingType={OpeningType.Push}
            drawerWidth={dims.drawerWidth}
            drawerHeight={dims.drawerHeight}
            drawerDepth={dims.drawerDepth}
            selectedColor={color}
            drawerIndex={index}
            positionX={positionX}
            positionY={drawerPositionY}
            isHovered={hoveredIndex === index}
            rotateTexture={false}
          />
        </group>
      ))}
    </>
  )
}

const WorkTableLights = getProduct('work-table').Lights

const WorkTableScene: React.FC<WorkTableViewerProps> = ({
  width,
  depth,
  height,
  selectedColor,
  hasDrawers = false,
  drawerCount = 3,
}) => {
  const color = useMemo(() => {
    if (!selectedColor) return FALLBACK_COLOR
    return getColorItemByName(selectedColor)?.hexCode ?? FALLBACK_COLOR
  }, [selectedColor])

  return (
    <>
      {/*<fog attach="fog" args={['#eae9e8', 5000, 12000]} />*/}

      <WorkTableLights enableShadows shadowQuality="high" />

      <GroundShadow
        width={width + 5500}
        depth={depth + 5500}
        opacity={0.25}
        blur={1.5}
      />

      <Suspense fallback={null}>
        <GLBModel
          modelUrl="/cdn/3d/room7-optimized.glb"
          modelPosition={[-40, -10, 580]}
          modelScale={[120, 120, 120]}
          modelRotation={[0 , 0,0]}
          shouldCastShadow={true}
          shouldReceiveShadow={true}
          userData={{ isRoom: true }}
        />
      </Suspense>

      <Suspense fallback={null}>
        <WorkTableModel
          width={width}
          depth={depth}
          height={height}
          color={color}
          hasDrawers={hasDrawers}
          drawerCount={drawerCount}
        />
      </Suspense>
    </>
  )
}

interface SceneCaptureProps {
  rendererRef: RefObject<THREE.WebGLRenderer | null>
  sceneRef: RefObject<THREE.Scene | null>
  cameraRef: RefObject<THREE.Camera | null>
}

const SceneCapture: React.FC<SceneCaptureProps> = ({
  rendererRef,
  sceneRef,
  cameraRef,
}) => {
  const { gl, scene, camera } = useThree()
  useEffect(() => {
    rendererRef.current = gl
    sceneRef.current = scene
    cameraRef.current = camera
  }, [gl, scene, camera, rendererRef, sceneRef, cameraRef])
  return null
}

export const WorkTableViewer = forwardRef<
  WorkTableViewerRef,
  WorkTableViewerProps
>(
  (
    {
      width,
      depth,
      height,
      selectedColor,
      hasDrawers = false,
      drawerCount = 3,
      onShare,
      onExport,
    },
    ref
  ) => {
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
    const sceneRef = useRef<THREE.Scene | null>(null)
    const cameraRef = useRef<THREE.Camera | null>(null)

    const orbitRef = useRef<OrbitControlsImpl>(null)

    const debug = useSceneDebug()

    const isDebugMode = useMemo(() => {
      if (typeof window === 'undefined') return false
      const v = new URLSearchParams(window.location.search).get('debug')
      return v === '1' || v === 'true'
    }, [])

    const handleZoomIn = useCallback(() => {
      const controls = orbitRef.current
      if (!controls) return
      const dir = controls.object.position.clone().sub(controls.target).normalize()
      const dist = controls.object.position.distanceTo(controls.target)
      const newDist = Math.max(controls.minDistance, dist * 0.75)
      controls.object.position.copy(controls.target.clone().addScaledVector(dir, newDist))
      controls.update()
    }, [])

    const handleZoomOut = useCallback(() => {
      const controls = orbitRef.current
      if (!controls) return
      const dir = controls.object.position.clone().sub(controls.target).normalize()
      const dist = controls.object.position.distanceTo(controls.target)
      const newDist = Math.min(controls.maxDistance, dist * 1.33)
      controls.object.position.copy(controls.target.clone().addScaledVector(dir, newDist))
      controls.update()
    }, [])

    useImperativeHandle(ref, () => ({
      captureScreenshot: (
        format: 'image/png' | 'image/jpeg' = 'image/jpeg',
        quality = 0.85
      ) => {
        if (!rendererRef.current || !sceneRef.current || !cameraRef.current) {
          return null
        }
        const scene = sceneRef.current
        const prevBg = scene.background
        scene.background = new THREE.Color('#eae9e8')

        const result = captureScreenshot(
          rendererRef.current,
          scene,
          cameraRef.current,
          format,
          quality
        )

        scene.background = prevBg
        return result
      },
      exportModel: async () => {
        const scene = sceneRef.current
        if (!scene) return

        const glbData = await exportSceneAsGLB(scene)
        triggerDownload(glbData, 'work-table-config.glb')
      },
    }))

    return (
      <WebGLErrorBoundary>
        <div className={styles.container}>
          <Canvas
            camera={CAMERA_CONFIG}
            shadows
            dpr={[1, 1.75]}
            gl={{
              powerPreference: 'high-performance',
              preserveDrawingBuffer: true,
              alpha: true,
            }}
            className={styles.canvas}
            style={{ background: 'transparent', minHeight: '100%' }}
          >
            <SceneCapture
              rendererRef={rendererRef}
              sceneRef={sceneRef}
              cameraRef={cameraRef}
            />

            <OrbitControls
              ref={orbitRef}
              makeDefault
              enablePan
              enableZoom={isDebugMode}
              enableRotate
              enableDamping={false}
              {...(isDebugMode
                ? { minDistance: 0.01, maxDistance: Infinity }
                : ORBIT_CONFIG)}
              target={[0, height / 2, 0]}
            />

            <WorkTableScene
              width={width}
              depth={depth}
              height={height}
              selectedColor={selectedColor}
              hasDrawers={hasDrawers}
              drawerCount={drawerCount}
            />

            {debug.canvasNode}
          </Canvas>
          {debug.overlayNode}
          <ModelLoadingOverlay />
          <ViewerZoomControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onShare={onShare} onExport={onExport} />
        </div>
      </WebGLErrorBoundary>
    )
  }
)

WorkTableViewer.displayName = 'WorkTableViewer'
